import React from "react";
import { useTranslation } from "react-i18next";
import { usePergunta } from "../contexts/PerguntaContext";
import ObterHistoricoConversa from "../hooks/ObterHistoricoConversa";
import { useTextosInterface } from "../hooks/TextosInterface";
import { resumirConversa } from "../lib/resumoConversa";
import { useUser } from "../empreenderUI/UserContext";
import { obterAcessoHumano } from "../lib/acessoHumano";
import CartaoSemPlano from "./CartaoSemPlano";
import { focarCampoDuvida } from "./BarraResolveu";
import { registrarEvento } from "../lib/metricas";
import { whatsAppDaPagina } from "../lib/whatsappDaPagina";

interface WhatsApp {
  img: string;
  nome: string;
  ultimostatus: string;
  whatsapp: string;
}

interface CartaoEncaminhamentoProps {
  whatsapps: WhatsApp[];
  slugAtual: string;
}

const FOTO_PADRAO = "https://app.sak.com.br/assets/img/profile/Asset 1.svg";

// Encaminhamento para o atendente: mostra o resumo do caso e abre o WhatsApp com ele já
// escrito, para o atendente não começar do zero.
const CartaoEncaminhamento: React.FC<CartaoEncaminhamentoProps> = ({
  whatsapps,
  slugAtual,
}) => {
  const { t } = useTranslation();
  const { appName, conversaAtual } = usePergunta();
  const historico = ObterHistoricoConversa(slugAtual, conversaAtual);
  const textosInterface = useTextosInterface();
  const acesso = obterAcessoHumano(useUser()?.user);

  if (acesso === "sem_plano") {
    return <CartaoSemPlano aoContinuarComIa={focarCampoDuvida} />;
  }

  const resumo = resumirConversa(historico, textosInterface);

  // Resumo escrito pela IA da central, quando ela é quem sugeriu o atendente.
  const resumoIA = [...historico].reverse().find((m) => m.tipo === "assistente" && m.resumoCaso)?.resumoCaso;

  const linhas = [
    `${t("resumo_app")}: ${appName}`,
    resumoIA && `${t("resumo_caso")}: ${resumoIA}`,
    !resumoIA && resumo.duvida && `${t("resumo_duvida")}: ${resumo.duvida}`,
    !resumoIA &&
      resumo.detalhes.length > 0 &&
      `${t("resumo_detalhes")}: ${resumo.detalhes.join(" / ")}`,
    resumo.textoPrint &&
      `${t("resumo_print")}: ${resumo.textoPrint.replace(/\s*\n\s*/g, " / ").slice(0, 300)}`,
    resumo.respostasIa > 0 &&
      `${t("resumo_tentativas")}: ${t("resumo_respostas_ia", { count: resumo.respostasIa })}${
        resumo.naoResolveu ? ` (${t("resumo_nao_resolveu")})` : ""
      }`,
  ].filter(Boolean) as string[];

  // Vindo de uma landing page, a mensagem vai para o time de vendas da página.
  const saudacao = whatsAppDaPagina() ? t("resumo_saudacao_lp", { app: appName }) : t("resumo_saudacao");
  const textoWhatsApp = `${saudacao}\n\n${linhas.join("\n")}`;

  // Métricas: o painel mostra o que o atendente recebeu (sem a linha do app, que já está no registro).
  const registrarEnvio = () =>
    registrarEvento({ tipo: "whatsapp", conversa: conversaAtual, app: slugAtual, resumo: linhas.slice(1).join("\n") });

  return (
    <div className="cartao-suporte cartao-encaminhamento">
      <p className="cartao-suporte-titulo">{t("encaminhar_titulo")}</p>
      <p className="cartao-suporte-texto">{t("encaminhar_texto")}</p>

      <ul className="resumo-caso">
        {linhas.map((linha) => (
          <li key={linha}>{linha}</li>
        ))}
      </ul>

      {whatsapps.length > 0 ? (
        <div className="contatos-whatsapp">
          {whatsapps.map((contato) => (
            <a
              key={contato.whatsapp}
              className="contato-whatsapp"
              href={`https://api.whatsapp.com/send?phone=${contato.whatsapp}&text=${encodeURIComponent(textoWhatsApp)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={registrarEnvio}
            >
              <img
                src={contato.img || FOTO_PADRAO}
                alt=""
                onError={(e) => (e.currentTarget.src = FOTO_PADRAO)}
              />
              <span>{t("falar_com_no_whatsapp", { nome: contato.nome })}</span>
            </a>
          ))}
        </div>
      ) : (
        <p className="cartao-suporte-aviso">{t("nenhum_whatsapp_disponivel")}</p>
      )}
    </div>
  );
};

export default CartaoEncaminhamento;
